// ────────────────────────────────────────────────────────────
// useDeal — hook to read the active deal + its data
// ────────────────────────────────────────────────────────────

import { useEffect } from 'react';
import { useDealsStore, useDealData } from '../store/deals';
import { useConversationStore } from '../store/conversation';
import { useUIStore } from '../store/ui';
import type { ConversationMessage } from '../types';

// Stable singleton — same reference every render to keep Zustand selectors quiet.
const EMPTY_CONVO: ConversationMessage[] = Object.freeze([]) as unknown as ConversationMessage[];

export function useDeal(dealId: string | null) {
  const deal = useDealsStore(s => dealId ? s.deals.find(d => d.id === dealId) ?? null : null);
  const data = useDealData(dealId);
  const buyers = useDealsStore(s => s.buyers);
  const conversation = useConversationStore(s => dealId ? (s.byDealId[dealId] ?? EMPTY_CONVO) : EMPTY_CONVO);
  const isStreaming = useConversationStore(s => dealId ? !!s.isStreaming[dealId] : false);
  const streamingDraft = useConversationStore(s => dealId ? (s.streamingDraft[dealId] ?? '') : '');
  const pinnedTab = useUIStore(s => dealId ? s.centerTab[dealId] : undefined);
  const centerTab = pinnedTab ?? deal?.pinnedTab ?? 'overview';

  const loadDealData = useDealsStore(s => s.loadDealData);
  const loadConversation = useConversationStore(s => s.load);

  useEffect(() => {
    if (dealId) {
      void loadDealData(dealId);
      void loadConversation(dealId);
    }
  }, [dealId, loadDealData, loadConversation]);

  return { deal, data, buyers, conversation, isStreaming, streamingDraft, centerTab };
}
